import { memo, useCallback, useRef, useMemo, useState, useEffect } from 'react';
import './Navigation.css';

const baseMenuItems = [
  { id: 'map', label: 'Live!', icon: '/icons/map.png' },
  { id: 'history', label: 'History', icon: '/icons/history.svg' },
  // Predictions absorbed the Airport page (arrivals/departures/on-ground)
  // — see PredictionsOnGround. The standalone "airport" route still
  // resolves in App.js but is no longer exposed in nav.
  { id: 'predictions', label: 'Predictions', icon: '/icons/history.svg' },
  { id: 'messages', label: 'ACARS/VDL2', icon: '/icons/messages.svg' },
  { id: 'airband', label: 'Airband', icon: '/icons/messages.svg' },
  { id: 'statistics', label: 'Statistics', icon: '/icons/history.svg' },
  { id: 'notams', label: 'NOTAMs', icon: '/icons/notam.svg' },
  { id: 'account', label: 'Account', icon: '/icons/person.png' },
  { id: 'receivers', label: 'Receivers', icon: '/icons/location.png' },
  { id: 'downloads', label: 'Downloads', icon: '/icons/download.svg' },
  { id: 'settings', label: 'Settings', icon: '/icons/settings.svg' }
];

const adminMenuItem = { id: 'admin', label: 'Admin', icon: '/icons/admin.svg' };
const billingMenuItem = { id: 'billing', label: 'Billing', icon: '/icons/admin.svg' };
const geospatialMenuItem = { id: 'geospatial', label: 'Geospatial', icon: '/icons/admin.svg' };

// Landscape iPads get this desktop nav (App.js classifies them as desktop),
// but Predictions/Billing are desktop-workstation pages — hide them on any
// iPad. Same UA test App.js uses for layout detection.
const IS_IPAD = typeof navigator !== 'undefined'
  && navigator.maxTouchPoints > 1
  && /Macintosh|iPad/.test(navigator.userAgent);

// Individual nav button - memoized to prevent re-renders
const NavButton = memo(function NavButton({ item, isActive, onPageChange, inMobileMenu = false }) {
  // Use ref to store callback to avoid stale closure issues during rapid re-renders
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Use ref to always get latest callback
    if (onPageChangeRef.current) {
      onPageChangeRef.current(item.id);
    }
  }, [item.id]);

  // Handle touch events directly to ensure they're captured before re-renders
  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onPageChangeRef.current) {
      onPageChangeRef.current(item.id);
    }
  }, [item.id]);

  return (
    <button
      className={`nav-item ${isActive ? 'active' : ''} ${inMobileMenu ? 'mobile-menu-item' : ''}`}
      onClick={handleClick}
      onTouchEnd={handleTouchEnd}
    >
      <img src={item.icon} alt={item.label} className="nav-icon" />
      <span className="nav-label">{item.label}</span>
    </button>
  );
});

function Navigation({ currentPage, onPageChange, userRoles = [], isViewerOnly = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef(null);

  // Check for mobile layout
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close menu when page changes
  const handlePageChange = useCallback((pageId) => {
    setMobileMenuOpen(false);
    onPageChange(pageId);
  }, [onPageChange]);

  // Build menu items based on user roles and viewer status
  const menuItems = useMemo(() => {
    let items = [...baseMenuItems];

    // Hide Account and Receivers for viewer-only users
    if (isViewerOnly) {
      items = items.filter(item => !['account', 'receivers'].includes(item.id));
    }

    // Predictions is desktop-only (see IS_IPAD note above)
    if (IS_IPAD) {
      items = items.filter(item => item.id !== 'predictions');
    }

    // Add admin-only menu items if user has ADMIN role
    if (userRoles.includes('ADMIN')) {
      items.push(adminMenuItem);
      if (!IS_IPAD) {
        items.push(billingMenuItem);
      }
      items.push(geospatialMenuItem);
    }
    return items;
  }, [userRoles, isViewerOnly]);

  // Get current page label for mobile display
  const currentPageLabel = useMemo(() => {
    const item = menuItems.find(i => i.id === currentPage);
    return item ? item.label : 'Menu';
  }, [menuItems, currentPage]);

  const toggleMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <nav className={`navigation ${mobileMenuOpen ? 'menu-open' : ''}`} ref={menuRef}>
      {/* Hamburger button - visible on mobile only */}
      {isMobile && (
        <button
          className="hamburger-btn"
          onClick={toggleMenu}
          onTouchEnd={(e) => { e.preventDefault(); toggleMenu(); }}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <span className="hamburger-icon">
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          </span>
          <span className="current-page-label">{currentPageLabel}</span>
        </button>
      )}

      {/* Desktop/tablet navigation - always visible */}
      {/* Mobile dropdown menu - visible when open */}
      <div className={`nav-items ${isMobile ? 'mobile-dropdown' : ''} ${mobileMenuOpen ? 'open' : ''}`}>
        {menuItems.map(item => (
          <NavButton
            key={item.id}
            item={item}
            isActive={currentPage === item.id}
            onPageChange={handlePageChange}
            inMobileMenu={isMobile}
          />
        ))}
      </div>

      {/* Mobile menu backdrop */}
      {isMobile && mobileMenuOpen && (
        <div
          className="mobile-menu-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          onTouchEnd={(e) => { e.preventDefault(); setMobileMenuOpen(false); }}
        />
      )}
    </nav>
  );
}

// Memoize to prevent re-renders from parent data updates
export default memo(Navigation);